/**
 * Monthly Clinic Performance Report — Office Director-facing, printable.
 *
 * Feb 2026 polish v2: centered numeric columns, centered tiles, plain
 * narrative paragraph, print-optimised staff table, notes-as-plain-text
 * in print, landscape print orientation, no browser header/footer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Printer, RefreshCw, AlertTriangle, Save } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList,
} from 'recharts';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#463d4f', soft: '#685B72',
  border: '#E9E3DA', hair: '#B08A3A',
  paper: '#FBF8F4', paper2: '#F5EFDA',
  gold: '#B08A3A', teal: '#3F7D6E', amber: '#B08A3A', rose: '#8A3A3A', purple: '#685B72',
};

// Shared label + number classes so every uppercase label and every number
// on the page match font family, size and letter spacing.
const LABEL = 'text-[10px] uppercase tracking-[0.16em] font-semibold';
const NUM_CLS = 'tabular-nums';

const NUM = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const INT = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Math.round(Number(n)).toLocaleString());
const PCT = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_LABEL = (ymd) => {
  if (!ymd) return '—';
  const [y, m] = String(ymd).split('-');
  return `${MONTHS[Math.max(0, Math.min(11, Number(m) - 1))]} ${y}`;
};

const N_UNITS = (n, singular) => {
  if (n == null) return '—';
  const v = Math.abs(Number(n));
  return `${v.toLocaleString()} ${v === 1 ? singular : singular + 's'}`;
};

// ─────────────────────────────────────────────────────────────────
// Change badges
// ─────────────────────────────────────────────────────────────────
function CountChangeInline({ change, unit = 'session', showPct = false }) {
  if (!change || change.abs == null) return <span style={{ color: C.soft }}>—</span>;
  const color = change.better === true ? C.teal : change.better === false ? C.rose : C.soft;
  if (change.abs === 0) return <span style={{ color: C.soft }}>unchanged</span>;
  const dir = change.abs > 0 ? 'up' : 'down';
  const pct = showPct && change.pct != null ? ` (${Math.abs(change.pct).toFixed(1)}%)` : '';
  return (
    <span className={`font-semibold ${NUM_CLS}`} style={{ color }}>
      {dir} {N_UNITS(change.abs, unit)}{pct}
    </span>
  );
}

function PointsChangeInline({ change }) {
  if (!change || change.points == null) return <span style={{ color: C.soft }}>—</span>;
  const color = change.better === true ? C.teal : change.better === false ? C.rose : C.soft;
  if (change.points === 0) return <span style={{ color: C.soft }}>unchanged</span>;
  const dir = change.points > 0 ? 'up' : 'down';
  const unit = Math.abs(change.points) === 1 ? 'point' : 'points';
  return (
    <span className={`font-semibold ${NUM_CLS}`} style={{ color }}>
      {dir} {Math.abs(change.points).toFixed(1)} {unit}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    'Met': { bg: `${C.teal}22`, fg: C.teal },
    'On target': { bg: `${C.teal}22`, fg: C.teal },
    'Up': { bg: `${C.teal}22`, fg: C.teal },
    'Close': { bg: `${C.amber}22`, fg: C.amber },
    'Below': { bg: `${C.rose}22`, fg: C.rose },
    'Down': { bg: `${C.rose}22`, fg: C.rose },
    'PRN': { bg: '#EFECF4', fg: C.soft },
    'Contract': { bg: '#EFECF4', fg: C.soft },
    'Student': { bg: '#EFECF4', fg: C.soft },
    'Started': { bg: `${C.gold}22`, fg: C.gold },
    'Left': { bg: `${C.gold}22`, fg: C.gold },
    'No goal': { bg: '#EFECF4', fg: C.soft },
    'No data': { bg: '#EFECF4', fg: C.soft },
  };
  const s = map[status] || { bg: '#EFECF4', fg: C.soft };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {status || '—'}
    </span>
  );
}

// Section header — number on same baseline / size as heading.
function Section({ id, num, title, takeaway, children }) {
  return (
    <section id={id} className="mb-10 print:mb-5 mcr-section">
      <div className="flex items-baseline gap-2 mb-2 pb-2 border-b-2" style={{ borderColor: C.hair }}>
        <span className={`font-serif text-2xl ${NUM_CLS}`} style={{ color: C.gold }}>{num}.</span>
        <h2 className="font-serif text-2xl leading-none" style={{ color: C.ink }}>{title}</h2>
      </div>
      {takeaway && (
        <div className="text-[12px] mb-3" style={{ color: C.ink2 }} data-testid={`mcr-takeaway-${id}`}>
          {takeaway}
        </div>
      )}
      {children}
    </section>
  );
}

// Uniform, centered tile.
function Tile({ label, value, compare, tone }) {
  return (
    <div
      className="rounded-xl border px-4 py-3 flex flex-col items-center text-center h-full"
      style={{ borderColor: C.border, background: C.paper }}
    >
      <div className={LABEL} style={{ color: C.soft }}>{label}</div>
      <div className={`font-serif text-2xl mt-1 ${NUM_CLS} leading-tight`} style={{ color: tone || C.ink }}>{value}</div>
      <div className="text-[11px] mt-1" style={{ color: C.soft }}>{compare || '\u00a0'}</div>
    </div>
  );
}

// Attendance flow bar — legend on one line, evenly spaced.
function AttendanceBar({ sessions }) {
  const cur = sessions.current;
  const prior = sessions.prior;
  const segs = [
    { k: 'completed', label: 'Completed', color: C.teal, cur: cur.completed_sessions, prior: prior.completed_sessions },
    { k: 'family_cancels', label: 'Family cancels', color: '#C8A97E', cur: cur.family_cancels, prior: prior.family_cancels },
    { k: 'clinician_cancels', label: 'Clinician cancels', color: C.rose, cur: cur.clinician_cancels, prior: prior.clinician_cancels },
    { k: 'no_shows', label: 'No-shows', color: '#6B4E3D', cur: cur.no_shows, prior: prior.no_shows },
    { k: 'late_cancels', label: 'Late cancels', color: C.amber, cur: cur.late_cancels, prior: prior.late_cancels },
  ];
  const totalCur = segs.reduce((s, x) => s + (x.cur || 0), 0) || 1;
  const totalPrior = segs.reduce((s, x) => s + (x.prior || 0), 0) || 1;
  const renderBar = (label, total, key) => (
    <div>
      <div className="flex justify-between mb-1">
        <span className={LABEL} style={{ color: C.soft }}>{label}</span>
        <span className={`text-[10px] ${NUM_CLS} font-semibold`} style={{ color: C.ink }}>{total.toLocaleString()} scheduled</span>
      </div>
      <div className="flex h-6 rounded overflow-hidden border" style={{ borderColor: C.border }}>
        {segs.map(s => {
          const v = key === 'cur' ? s.cur : s.prior;
          const t = key === 'cur' ? totalCur : totalPrior;
          const pct = (v / t) * 100;
          if (pct < 0.5) return null;
          return <div key={s.k} style={{ width: `${pct}%`, background: s.color }} title={`${s.label}: ${v}`} />;
        })}
      </div>
    </div>
  );
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
      <div className="space-y-3">
        {renderBar('This month', totalCur, 'cur')}
        {renderBar('Last month', totalPrior, 'prior')}
      </div>
      <div className="grid grid-cols-5 gap-2 mt-3 pt-3 border-t" style={{ borderColor: C.border }}>
        {segs.map(s => (
          <div key={s.k} className="text-[10px] flex flex-col items-center text-center" style={{ color: C.ink2 }}>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
              <span className="whitespace-nowrap">{s.label}</span>
            </div>
            <div className={NUM_CLS}>
              <span className="font-semibold">{(s.cur || 0).toLocaleString()}</span>
              <span style={{ color: C.soft }}> (was {(s.prior || 0).toLocaleString()})</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 12-month sessions trend — bar chart with values on top of each bar
// and a trailing 6-month moving-average line overlaid. Months with no
// activity are already dropped server-side.
function SessionsTrendChart({ trend }) {
  if (!trend || !Array.isArray(trend.months) || trend.months.length === 0) {
    return null;
  }
  const data = trend.months.map((m) => ({
    label: m.short_label || m.label,
    full_label: m.label,
    sessions_total: m.sessions_total,
    yoy_sessions_total: m.yoy_sessions_total,
    trend_6m: m.trend_6m,
    is_current: m.is_current,
  }));
  const maxVal = Math.max(
    ...data.map((d) => Math.max(d.sessions_total || 0, d.yoy_sessions_total || 0))
  );
  const yMax = Math.ceil((maxVal * 1.18) / 50) * 50 || 100;
  const currentIdx = data.findIndex((d) => d.is_current);
  const yoyOn = !!trend.yoy_available;
  return (
    <div className="rounded-xl border p-4 mt-3" style={{ borderColor: C.border, background: C.paper }} data-testid="mcr-sessions-trend">
      <div className="flex items-baseline justify-between mb-2">
        <div className={`${LABEL}`} style={{ color: C.soft }}>12-month sessions trend</div>
        <div className="text-[10px]" style={{ color: C.soft }}>
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: C.gold }} />
            Monthly total
          </span>
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="inline-block w-5 h-0.5" style={{ background: C.teal }} />
            6-month trailing average
          </span>
          {yoyOn && (
            <span className="inline-flex items-center gap-1" data-testid="mcr-sessions-trend-yoy-legend">
              <span
                className="inline-block w-5 h-0"
                style={{ borderTop: `2px dotted ${C.purple}` }}
              />
              <span style={{ marginLeft: 2 }}>Year-ago sessions</span>
            </span>
          )}
        </div>
      </div>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 24, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.soft }} axisLine={{ stroke: C.border }} tickLine={false} />
            <YAxis domain={[0, yMax]} tick={{ fontSize: 10, fill: C.soft }} axisLine={{ stroke: C.border }} tickLine={false} width={38} />
            <Tooltip
              contentStyle={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
              labelFormatter={(_, payload) => (payload && payload[0]) ? payload[0].payload.full_label : ''}
              formatter={(v, name) => {
                if (v == null) return [null, null];
                if (name === 'sessions_total') return [Number(v).toLocaleString(), 'Sessions'];
                if (name === 'trend_6m') return [Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }), '6-mo avg'];
                if (name === 'yoy_sessions_total') return [Number(v).toLocaleString(), 'Year ago'];
                return [v, name];
              }}
            />
            <Bar dataKey="sessions_total" fill={C.gold} radius={[4, 4, 0, 0]} maxBarSize={40}
                 isAnimationActive={false}>
              <LabelList
                dataKey="sessions_total"
                position="top"
                formatter={(v) => Number(v).toLocaleString()}
                style={{ fontSize: 10, fill: C.ink, fontWeight: 600 }}
              />
            </Bar>
            {yoyOn && (
              <Line
                type="monotone"
                dataKey="yoy_sessions_total"
                stroke={C.purple}
                strokeWidth={1.75}
                strokeDasharray="4 3"
                dot={{ r: 2, fill: '#fff', stroke: C.purple, strokeWidth: 1.25 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
            <Line type="monotone" dataKey="trend_6m" stroke={C.teal} strokeWidth={2}
                  dot={{ r: 2.5, fill: C.teal, stroke: C.teal }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10.5px] italic mt-1" style={{ color: C.soft }}>
        {trend.definition}
        {currentIdx >= 0 && (
          <span> · {data[currentIdx].full_label} is the report month.</span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Note box: interactive on screen; plain text under label on print.
function NotesBox({ label, starter, quickPicks, value, onChange, testid }) {
  const append = (chip) => {
    const cur = value || '';
    const sep = cur.trim().length ? (cur.trim().endsWith('.') ? ' ' : '. ') : '';
    onChange(cur + sep + chip);
  };
  const hasContent = (value || '').trim().length > 0;
  return (
    <div className={`mt-4 mcr-note ${hasContent ? '' : 'print:hidden'}`}>
      {/* Screen view — interactive with dashed box and chips */}
      <div className="print:hidden rounded-xl border-2 border-dashed p-4" style={{ borderColor: C.hair, background: '#FFFDF7' }}>
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-1" style={{ color: C.gold }}>{label}</div>
        {starter && <div className="text-[11px] italic mb-2" style={{ color: C.soft }}>Starter: “{starter}”</div>}
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={starter}
          rows={3}
          className="w-full rounded-md border p-2 text-[12px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#B08A3A]/40 bg-white"
          style={{ borderColor: C.border, color: C.ink, resize: 'vertical' }}
          data-testid={testid}
        />
        {quickPicks && quickPicks.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`${LABEL} self-center`} style={{ color: C.soft }}>Quick reasons:</span>
            {quickPicks.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => append(p)}
                className="text-[11px] rounded-full px-2.5 py-0.5 border hover:bg-[#F5EFDA] transition"
                style={{ borderColor: C.border, color: C.ink2 }}
                data-testid={`${testid}-chip-${p.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Print view — plain label + paragraph, no box, no starter, no chips */}
      {hasContent && (
        <div className="hidden print:block">
          <div className={LABEL} style={{ color: C.ink2 }}>{label}</div>
          <div className="text-[11px] leading-snug whitespace-pre-wrap mt-1" style={{ color: C.ink }}>{value}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────────
export default function MonthlyClinicReport() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [availableClinics, setAvailableClinics] = useState([]);

  const clinicParam = params.get('clinic') || '';
  const monthParam = params.get('month') || defaultMonth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [notes, setNotes] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === 'super_user' || user?.role === 'admin' || user?.role === 'hr_payroll') {
      api.get('/clinics').then((r) => {
        const list = Array.isArray(r.data) ? r.data.map((c) => c.name || c.clinic || c).filter(Boolean) : [];
        setAvailableClinics(list);
      }).catch(() => {});
    } else if (user?.role === 'office_director') {
      const list = [...(user.clinics || []), user.clinic].filter(Boolean);
      setAvailableClinics(Array.from(new Set(list)));
      if (!clinicParam && list.length) {
        setParams({ clinic: list[0], month: monthParam }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = useCallback(() => {
    if (!clinicParam || !monthParam) return;
    setLoading(true); setErr(null);
    api.get('/monthly-clinic-report', { params: { clinic: clinicParam, month: monthParam } })
      .then((r) => {
        setData(r.data);
        setNotes(r.data?.notes || {});
        setDirty(false);
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [clinicParam, monthParam]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    const prev = document.title;
    document.title = `Thrive Speech - ${data.clinic} - ${data.month_label} report`;
    return () => { document.title = prev; };
  }, [data]);

  const onNoteChange = (field) => (v) => { setNotes((s) => ({ ...s, [field]: v })); setDirty(true); };

  const saveNotes = async () => {
    if (!clinicParam || !monthParam) return;
    setSaving(true);
    try {
      await api.put('/monthly-clinic-report/notes', { clinic: clinicParam, month: monthParam, ...notes });
      toast.success('Notes saved');
      setDirty(false);
    } catch (e) {
      toast.error('Could not save notes: ' + (e?.response?.data?.detail || 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const doPrint = async () => {
    if (dirty) await saveNotes();
    setTimeout(() => window.print(), 200);
  };

  const monthOptions = useMemo(() => {
    const now = new Date();
    const out = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);

  if (!user) return null;
  if (!['super_user', 'admin', 'hr_payroll', 'office_director'].includes(user.role)) {
    return <div className="p-12 text-center" style={{ color: C.rose }} data-testid="mcr-forbidden">This report is restricted to Office Directors and Super Users.</div>;
  }

  return (
    <div className="min-h-screen" style={{ background: '#FFFFFF' }} data-testid="mcr-root">
      <div className="print:hidden sticky top-0 z-20 backdrop-blur bg-white/90 border-b" style={{ borderColor: C.border }}>
        <div className="max-w-[1100px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
          <div className={`${LABEL} font-bold`} style={{ color: C.gold }}>Monthly Clinic Performance Report</div>
          <div className="flex-1" />
          <label htmlFor="mcr-clinic" className={LABEL} style={{ color: C.soft }}>Clinic</label>
          <select id="mcr-clinic" data-testid="mcr-clinic-select" value={clinicParam}
            onChange={(e) => setParams({ clinic: e.target.value, month: monthParam }, { replace: true })}
            className="h-9 px-3 rounded-full border text-[13px] font-medium bg-white"
            style={{ borderColor: C.hair, color: C.ink }}>
            <option value="">— select —</option>
            {availableClinics.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label htmlFor="mcr-month" className={LABEL} style={{ color: C.soft }}>Month</label>
          <select id="mcr-month" data-testid="mcr-month-select" value={monthParam}
            onChange={(e) => setParams({ clinic: clinicParam, month: e.target.value }, { replace: true })}
            className="h-9 px-3 rounded-full border text-[13px] font-medium bg-white"
            style={{ borderColor: C.hair, color: C.ink }}>
            {monthOptions.map((m) => <option key={m} value={m}>{MONTH_LABEL(m)}</option>)}
          </select>
          <button type="button" onClick={saveNotes} disabled={!dirty || saving}
            className="rounded-full h-9 px-4 inline-flex items-center gap-1.5 text-[12px] font-semibold border transition disabled:opacity-40"
            style={{ borderColor: C.border, color: dirty ? C.gold : C.soft }} data-testid="mcr-save-btn">
            <Save size={13} /> {saving ? 'Saving…' : (dirty ? 'Save notes' : 'Notes saved')}
          </button>
          <button type="button" onClick={load}
            className="rounded-full h-9 px-4 inline-flex items-center gap-1.5 text-[12px] font-semibold border"
            style={{ borderColor: C.border, color: C.ink }} data-testid="mcr-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </button>
          <button type="button" onClick={doPrint}
            className="rounded-full h-9 px-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white"
            style={{ background: C.gold }} data-testid="mcr-print-btn">
            <Printer size={13} /> Print / PDF
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-10 print:px-0 print:py-0" data-testid="mcr-body">
        {loading && <div className="p-12 text-center" style={{ color: C.soft }} data-testid="mcr-loading">Generating report…</div>}
        {err && <div className="p-10 flex items-center gap-2 text-sm" style={{ color: C.rose }} data-testid="mcr-error"><AlertTriangle size={16} /> {err}</div>}
        {!loading && !err && !clinicParam && (
          <div className="p-12" data-testid="mcr-choose-clinic">
            <div className="font-serif text-2xl" style={{ color: C.ink }}>Choose a clinic to begin</div>
            <div className="text-[12px] mt-2" style={{ color: C.soft }}>Pick your clinic and the month you want to review from the toolbar above.</div>
          </div>
        )}
        {data && !loading && !err && (
          <ReportBody data={data} notes={notes} onNoteChange={onNoteChange} />
        )}
      </div>

      <style>{`
        /* Print rules — landscape orientation gives the staff table room
           to fit all columns. No browser header/footer will still depend
           on the user's Chrome dialog "Options → Headers and footers"
           checkbox, but we set @page margins tight to minimise them. */
        @media print {
          @page { size: letter landscape; margin: 0.15in 0.35in; }
          html, body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          .mcr-section { break-inside: avoid; page-break-inside: avoid; }
          table { break-inside: auto; width: 100% !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          .mcr-end { break-before: avoid; page-break-before: avoid; margin-top: 12pt !important; padding-top: 8pt !important; }
          .mcr-body { font-size: 10pt; }
          table th, table td { padding: 2px 4px !important; font-size: 8.5pt !important; }
          table th { font-size: 7pt !important; letter-spacing: 0 !important; }
          [data-testid="mcr-staff-table"] { table-layout: fixed !important; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Body — 6 sections, single left edge, uniform layout.
// ─────────────────────────────────────────────────────────────────
function ReportBody({ data, notes, onNoteChange }) {
  const cur = data.sessions.current;
  const prior = data.sessions.prior;
  const util = data.utilization;
  const staff = data.staff;
  const nc = data.new_clients;
  const pending = data.pending_notes;
  const yoyOn = !!data.yoy_available;
  const takeaways = data.takeaways || {};

  return (
    <div className="mcr-body">
      {/* Cover — left-aligned. */}
      <div className="pb-8 mb-8 border-b" style={{ borderColor: C.border }} data-testid="mcr-cover">
        <img src="/thrive-logo.webp" alt="Thrive Speech Therapy" className="mb-4"
             style={{ height: 60, width: 'auto' }} data-testid="mcr-logo" />
        <div className={`${LABEL} font-bold mb-2`} style={{ color: C.gold, letterSpacing: '0.32em' }}>Thrive Speech Therapy · Monthly Clinic Performance Report</div>
        <h1 className="font-serif leading-tight" style={{ color: C.ink, fontSize: 'clamp(28px, 4.5vw, 40px)' }}>{data.clinic}</h1>
        <div className="mt-1 font-serif text-2xl" style={{ color: C.ink2 }}>{data.month_label}</div>
        {data.director_name && <div className="mt-3 text-[13px]" style={{ color: C.soft }}>Prepared by <span className="font-semibold" style={{ color: C.ink2 }}>{data.director_name}</span> · Office Director</div>}
        <div className="text-[11px] mt-1" style={{ color: C.soft }}>
          Generated {new Date(data.generated_at).toLocaleString()} · vs last month {data.prior_month_label}
          {yoyOn && ` · vs same month last year (${data.yoy_month_label})`}
        </div>
        {!yoyOn && (
          <div className="text-[11px] italic mt-1" style={{ color: C.soft }} data-testid="mcr-yoy-note">
            {data.yoy_note}
          </div>
        )}
      </div>

      {/* 1) Summary — narrative paragraph IS the takeaway (no separate line) */}
      <Section id="summary" num={1} title="Summary">
        <p className="text-[13px] leading-relaxed mb-4" style={{ color: C.ink2 }} data-testid="mcr-narrative">
          {data.narrative}
        </p>

        <ScorecardTable rows={data.scorecard} yoyOn={yoyOn} />

        <NotesBox label="Director's opening remarks"
          starter="This month at our clinic, the story is…"
          quickPicks={['Growth held', 'Growth slipped', 'Utilization improved', 'Staff steady', 'Staff coaching needed']}
          value={notes.executive_summary} onChange={onNoteChange('executive_summary')} testid="mcr-notes-exec" />
      </Section>

      {/* 2) Sessions & Growth */}
      <Section id="sessions" num={2} title="Sessions & Growth" takeaway={takeaways.sessions}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile
            label="Total sessions"
            value={INT(cur.total_sessions)}
            compare={<>Last month {INT(prior.total_sessions)} · <CountChangeInline change={data.sessions.total_change} unit="session" showPct /></>}
          />
          <Tile
            label="Completed sessions"
            value={INT(cur.completed_sessions)}
            compare={<>Last month {INT(prior.completed_sessions)} · <CountChangeInline change={data.sessions.completed_change} unit="session" showPct /></>}
          />
          <Tile
            label="Family attendance"
            value={PCT(data.attendance.family_attendance.current)}
            tone={(data.attendance.family_attendance.current || 0) >= 0.80 ? C.teal : C.rose}
            compare={<>Last month {PCT(data.attendance.family_attendance.prior)} · <PointsChangeInline change={data.attendance.family_attendance.change} /></>}
          />
          <Tile
            label="Clinician cancellations"
            value={INT(data.attendance.clinician_cancels.current)}
            compare={<>Last month {INT(data.attendance.clinician_cancels.prior)} · <CountChangeInline change={data.attendance.clinician_cancels.change} unit="session" /></>}
          />
        </div>
        <div className="text-[11px] italic mt-2 mb-3" style={{ color: C.soft }}>
          Total sessions = completed + no-shows + late cancels. Family attendance excludes clinician cancellations.
        </div>

        <AttendanceBar sessions={data.sessions} />

        <SessionsTrendChart trend={data.sessions_trend} />

        <NotesBox label="Why families canceled"
          starter="The main reason families canceled was…"
          quickPicks={['Illness', 'Travel', 'School schedule', 'Insurance', 'Transportation']}
          value={notes.family_cancel_reasons} onChange={onNoteChange('family_cancel_reasons')}
          testid="mcr-notes-family-cancels" />
        <NotesBox label="Why clinicians canceled"
          starter="Clinician cancellations were driven by…"
          quickPicks={['Clinician out sick', 'Personal time off', 'Training', 'Coverage gap', 'One-off scheduling']}
          value={notes.clinician_cancel_reasons} onChange={onNoteChange('clinician_cancel_reasons')}
          testid="mcr-notes-clinician-cancels" />
      </Section>

      {/* 3) Staff */}
      <Section id="staff" num={3} title="Staff" takeaway={takeaways.staff}>
        <div className="flex flex-wrap items-center gap-2 mb-2 text-[10px]" data-testid="mcr-staff-legend">
          <span className={LABEL} style={{ color: C.soft }}>Legend:</span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: EMP_FILL.OD, border: `1px solid ${C.hair}` }} />
            <span style={{ color: C.ink2 }}><span className="font-bold">Office Director</span> (bold)</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: EMP_FILL.FT, border: `1px solid ${C.border}` }} />
            <span style={{ color: C.ink2 }}>Full-time</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: EMP_FILL.PT, border: `1px solid ${C.border}` }} />
            <span style={{ color: C.ink2 }}>Part-time</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: EMP_FILL.PRN, border: `1px solid ${C.border}` }} />
            <span style={{ color: C.ink2 }}>PRN / Extern</span>
          </span>
          <span className="mx-1" style={{ color: C.border }}>·</span>
          <span style={{ color: C.soft }}>Red fill = below weekly target · Yellow fill = close</span>
        </div>
        <div className="text-[10px] mb-2" style={{ color: C.soft }} data-testid="mcr-notes-as-of">
          Notes-waiting counts as of {pending.as_of}. <span className="print:hidden">Tap a clinician to see their week-by-week sessions vs target.</span>
        </div>
        <StaffTable staff={staff} pendingAsOf={pending.as_of}
          weekNotes={notes.week_notes || {}}
          onWeekNote={(key, v) => onNoteChange('week_notes')({ ...(notes.week_notes || {}), [key]: v })} />
        <ShortWeekReasons rows={staff.rows || []} weekNotes={notes.week_notes || {}} />
        <div className="mt-3 rounded-xl border p-3 text-[11px]" style={{ borderColor: C.border, background: C.paper }} data-testid="mcr-notes-rules">
          <span className="font-semibold" style={{ color: C.ink2 }}>Weekly attribution: </span>
          <span style={{ color: C.soft }} data-testid="mcr-week-attribution-rule">{staff.week_attribution_rule}</span>
          <span className="mx-2" style={{ color: C.border }}>·</span>
          <span className="font-semibold" style={{ color: C.ink2 }}>Overdue rule: </span>
          <span style={{ color: C.soft }}>{pending.overdue_rule}</span>
          <span className="mx-2" style={{ color: C.border }}>·</span>
          <span className="font-semibold" style={{ color: C.ink2 }}>Large backlog flag: </span>
          <span style={{ color: C.soft }}>{pending.large_backlog_threshold}+ outstanding notes even without any overdue.</span>
        </div>
        <NotesBox label="Staff coaching and plans"
          starter="The main coaching focus this month is…"
          quickPicks={['Weekly target review', 'Schedule adjustments', 'Documentation catch-up', 'Onboarding support', 'PTO / coverage']}
          value={notes.staff_notes} onChange={onNoteChange('staff_notes')} testid="mcr-notes-staff" />
      </Section>

      {/* 4) Utilization */}
      <Section id="utilization" num={4} title="Utilization" takeaway={takeaways.utilization}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile
            label="Utilization this month"
            value={PCT(util.current.pct)}
            tone={(util.current.pct || 0) >= util.target_pct ? C.teal : C.rose}
            compare={<>{INT(util.current.counted_sessions)} of {INT(util.current.capacity)} capacity</>}
          />
          <Tile label="Target" value={PCT(util.target_pct)} compare="Company target" />
          <Tile
            label="Last month"
            value={PCT(util.prior.pct)}
            compare={<PointsChangeInline change={util.change} />}
          />
          <Tile
            label="Gap vs target"
            value={util.current.pct == null ? '—' : (() => {
              const g = Math.round((util.current.pct * 100 - util.target_pct * 100) * 10) / 10;
              return `${g.toFixed(1)} points`;
            })()}
            tone={(util.current.pct || 0) >= util.target_pct ? C.teal : C.rose}
            compare={(util.current.pct || 0) >= util.target_pct ? 'Above target' : 'Below target'}
          />
        </div>
        <div className="text-[11px] italic mt-2 mb-3" style={{ color: C.soft }}>
          {util.definition}
        </div>
        {util.current.no_goal_sessions > 0 && (
          <div className="rounded-xl border p-4" style={{ borderColor: C.border, background: C.paper }} data-testid="mcr-no-goal-block">
            <div className={`${LABEL} mb-2`} style={{ color: C.soft }}>Sessions from people without a weekly goal</div>
            <div className="text-[12px]" style={{ color: C.ink2 }}>
              <span className="font-semibold">{INT(util.current.no_goal_sessions)} sessions</span> from{' '}
              {util.current.no_goal_clinicians.map((c, i) => (
                <span key={c.name}>
                  {i > 0 && ', '}
                  <span className="font-medium">{c.name}</span>{' '}
                  <span style={{ color: C.soft }}>({INT(c.completed_sessions)})</span>
                </span>
              ))} — listed here for transparency, not counted in utilization.
            </div>
          </div>
        )}
        <NotesBox label="What's holding utilization back"
          starter="Utilization is where it is because…"
          quickPicks={['Open positions', 'Referral pipeline', 'Cancellation rate', 'Room capacity', 'Insurance delays']}
          value={notes.utilization_notes} onChange={onNoteChange('utilization_notes')} testid="mcr-notes-utilization" />
      </Section>

      {/* 5) New Clients */}
      <Section id="new-clients" num={5} title="New Clients" takeaway={takeaways.new_clients}>
        <NewClientsTable nc={nc} yoyOn={yoyOn} />
        <div className="text-[11px] italic mt-2" style={{ color: C.soft }}>
          {nc.referral_to_eval.definition}
        </div>
        <NotesBox label="New-client notes"
          starter="This month's new-client story is…"
          quickPicks={['Referral spike', 'Referral slowdown', 'Eval scheduling gap', 'Fast-tracked starts', 'Insurance delays']}
          value={notes.new_clients_notes} onChange={onNoteChange('new_clients_notes')} testid="mcr-notes-new-clients" />
      </Section>

      {/* 6) Pending Notes */}
      <Section id="pending-notes" num={6} title="Pending Notes" takeaway={takeaways.pending_notes}>
        <div className="text-[10px] mb-2" style={{ color: C.soft }}>
          Counts as of {pending.as_of}. {pending.overdue_rule} Large-backlog flag triggers at {pending.large_backlog_threshold}+ outstanding notes.
        </div>
        <PendingNotesTable pending={pending} />
        <NotesBox label="Documentation focus"
          starter="For pending notes, the plan is…"
          quickPicks={['Same-week close', 'Overdue cleanup', 'Template review', 'Backup coverage']}
          value={notes.documentation_notes} onChange={onNoteChange('documentation_notes')} testid="mcr-notes-docs" />
      </Section>

      {/* 7) Director's Notes (closing) */}
      <Section id="directors-notes" num={7} title="Director's Notes"
        takeaway="Space to close out the month: what worked, what got in the way, and what changes next month.">
        <NotesBox label="What went well"
          starter="The bright spot this month was…"
          quickPicks={['Strong team week', 'Family retention', 'Referral partner win', 'Documentation on time', 'Onboarding smooth']}
          value={notes.what_went_well} onChange={onNoteChange('what_went_well')} testid="mcr-notes-wentwell" />
        <NotesBox label="Main obstacles"
          starter="The biggest challenge was…"
          quickPicks={['Cancellations', 'Staffing gap', 'Documentation load', 'Referral flow', 'Facility issue']}
          value={notes.main_obstacles} onChange={onNoteChange('main_obstacles')} testid="mcr-notes-obstacles" />
        <NotesBox label="Plan for next month"
          starter="Next month I'll focus on…"
          quickPicks={['Weekly target review', 'Family outreach', 'Cancellation call script', 'Documentation cleanup', 'New hire support']}
          value={notes.next_month_plan} onChange={onNoteChange('next_month_plan')} testid="mcr-notes-plan" />
      </Section>

      <div className="mcr-end text-[10px] mt-8 pt-6 border-t" style={{ color: C.soft, borderColor: C.border }}>
        End of report · Thrive Speech Therapy
      </div>
    </div>
  );
}

// ─── Scorecard table ─────────────────────────────────────────────
function ScorecardTable({ rows, yoyOn }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
      <table className="w-full text-[12px]" data-testid="mcr-scorecard">
        <thead>
          <tr className={`${LABEL}`} style={{ background: C.paper, color: C.soft }}>
            <th className="py-2 px-3 text-left whitespace-nowrap">Measure</th>
            <th className="py-2 px-3 text-left whitespace-nowrap">What we track</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">This month</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Last month</th>
            {yoyOn && <th className="py-2 px-3 text-center whitespace-nowrap">Last year</th>}
            <th className="py-2 px-3 text-center whitespace-nowrap">Target</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={s.name} className="border-t align-middle" style={{ borderColor: C.border }} data-testid={`mcr-scorecard-row-${i}`}>
              <td className="py-2 px-3 font-semibold whitespace-nowrap align-middle" style={{ color: C.ink }}>{s.name}</td>
              <td className="py-2 px-3 align-middle" style={{ color: C.soft }}>{s.measure}</td>
              <td className={`py-2 px-3 text-center ${NUM_CLS} font-semibold whitespace-nowrap align-middle`} style={{ color: C.ink }}>
                {s.format === 'pct' ? PCT(s.this_month) : (s.format === 'fraction' ? (s.this_month || '—') : INT(s.this_month))}
              </td>
              <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>
                {s.format === 'pct' ? PCT(s.last_month) : (s.format === 'fraction' ? (s.last_month || '—') : INT(s.last_month))}
              </td>
              {yoyOn && (
                <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>
                  {s.last_year == null ? '—' : (s.format === 'pct' ? PCT(s.last_year) : INT(s.last_year))}
                </td>
              )}
              <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>
                {s.target == null ? '—' : (s.format === 'pct' ? PCT(s.target) : String(s.target))}
              </td>
              <td className="py-2 px-3 text-center whitespace-nowrap align-middle"><StatusPill status={s.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Staff table — every non-label column centered ───────────────
// Row fills by employment type so a reader can scan FT vs PT vs PRN at a
// glance. Below/Close performance states take precedence as fills; OD rows
// always render in bold with the strongest gold tint and sort to the top.
const EMP_FILL = {
  OD:     '#F5EFDA', // warm gold tint — matches section underline color
  FT:     '#EEF4F2', // cool teal tint
  PT:     '#F3EFF6', // soft purple tint
  PRN:    '#F6F5F1', // neutral beige
  Extern: '#F6F5F1',
};
const EMP_LABEL = { OD: 'OD', FT: 'FT', PT: 'PT', PRN: 'PRN', Extern: 'Extern' };
const EMP_CHIP = {
  OD:     { bg: '#B08A3A33', fg: '#6E5420' },
  FT:     { bg: '#3F7D6E22', fg: '#2E5B50' },
  PT:     { bg: '#685B7222', fg: '#4A4154' },
  PRN:    { bg: '#EFECF4',   fg: '#685B72' },
  Extern: { bg: '#EFECF4',   fg: '#685B72' },
};

const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function WeekBreakdownRow({ p, i, weekNotes, onWeekNote }) {
  const weeks = p.weeks || [];
  return (
    <tr className="print:hidden" data-testid={`mcr-staff-weeks-${i}`} style={{ background: C.paper }}>
      <td colSpan={12} className="px-4 py-3">
        <div className="sticky left-4" style={{ maxWidth: 'min(1100px, calc(100vw - 64px))' }}>
        <div className={`${LABEL} mb-2`} style={{ color: C.soft }}>Week by week — {p.name}</div>
        {weeks.length === 0 ? (
          <div className="italic" style={{ color: C.soft }}>No weeks worked this month.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {weeks.map((w, j) => {
              const tone = w.met == null ? C.soft : (w.met ? C.teal : C.rose);
              return (
                <div key={w.start} data-testid={`mcr-staff-week-${i}-${j}`}
                  className="rounded-lg border px-3 py-2 w-full sm:w-auto sm:min-w-[140px] bg-white" style={{ borderColor: C.border }}>
                  <div className="text-[10px]" style={{ color: C.soft }}>
                    {fmtDay(w.start)} – {fmtDay(w.end)}{w.weekdays < 5 ? ` · ${w.weekdays} day${w.weekdays === 1 ? '' : 's'}` : ''}
                  </div>
                  <div className={`${NUM_CLS} text-[13px] font-semibold`} style={{ color: C.ink }}>
                    {w.shows}
                    {w.target != null && <span className="font-normal" style={{ color: C.soft }}> / {w.target}</span>}
                  </div>
                  <div className="text-[10px] font-semibold" style={{ color: tone }} data-testid={`mcr-staff-week-status-${i}-${j}`}>
                    {w.met == null ? 'No goal' : (w.met ? 'Met' : `Short ${Math.round((w.target - w.shows) * 10) / 10}`)}
                  </div>
                  {w.met === false && (
                    <input
                      type="text"
                      maxLength={200}
                      placeholder="Reason (optional)"
                      value={weekNotes[`${p.canon}|${w.start}`] || ''}
                      onChange={(e) => onWeekNote(`${p.canon}|${w.start}`, e.target.value)}
                      className="mt-1.5 w-full rounded border px-1.5 py-1 text-[10.5px] outline-none transition-colors focus:border-[#B08A3A]"
                      style={{ borderColor: C.border, color: C.ink }}
                      data-testid={`mcr-staff-week-note-${i}-${j}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </td>
    </tr>
  );
}

function ShortWeekReasons({ rows, weekNotes }) {
  const items = rows.flatMap((p) => (p.weeks || [])
    .filter((w) => w.met === false && (weekNotes[`${p.canon}|${w.start}`] || '').trim())
    .map((w) => ({ key: `${p.canon}|${w.start}`, name: p.name, w, note: weekNotes[`${p.canon}|${w.start}`].trim() })));
  if (!items.length) return null;
  return (
    <div className="mt-3 rounded-xl border p-3 text-[11px]" style={{ borderColor: C.border }} data-testid="mcr-short-week-reasons">
      <div className={`${LABEL} mb-1.5`} style={{ color: C.soft }}>Short-week reasons</div>
      <ul className="space-y-1">
        {items.map(({ key, name, w, note }) => (
          <li key={key} style={{ color: C.ink2 }} data-testid={`mcr-short-week-reason-${key}`}>
            <span className="font-semibold" style={{ color: C.ink }}>{name}</span>
            <span style={{ color: C.soft }}> · {fmtDay(w.start)} – {fmtDay(w.end)} ({w.shows} / {w.target})</span>
            : {note}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StaffTable({ staff, pendingAsOf, weekNotes, onWeekNote }) {
  const [openRow, setOpenRow] = useState(null);
  const rows = staff.rows || [];
  const totals = rows.reduce((acc, p) => ({
    monthly_goal: acc.monthly_goal + Math.round(p.monthly_goal || 0),
    total_sessions: acc.total_sessions + (p.total_sessions || 0),
    completed: acc.completed + (p.completed_sessions || 0),
    completed_goaled: acc.completed_goaled + (p.monthly_goal ? (p.completed_sessions || 0) : 0),
    clinician_cancels: acc.clinician_cancels + (p.clinician_cancels || 0),
    notes_outstanding: acc.notes_outstanding + (p.notes_outstanding || 0),
    notes_overdue: acc.notes_overdue + (p.notes_overdue || 0),
  }), { monthly_goal: 0, total_sessions: 0, completed: 0, completed_goaled: 0, clinician_cancels: 0, notes_outstanding: 0, notes_overdue: 0 });
  return (
    <div className="rounded-xl border overflow-x-auto print:overflow-visible" style={{ borderColor: C.border }}>
      <table className="w-full text-[11.5px]" data-testid="mcr-staff-table" style={{ tableLayout: 'auto' }}>
        <thead>
          <tr className={LABEL} style={{ background: C.paper, color: C.soft }}>
            <th className="py-2 px-2 text-left whitespace-nowrap">Clinician</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Weekly goal</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Weekly target met</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Avg weekly<br /><span className="normal-case text-[8px] font-normal" style={{ color: C.soft }}>(variance)</span></th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Monthly goal</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Total sessions</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Completed</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">% of goal</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Clin. cancels</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Notes waiting</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Overdue notes</th>
            <th className="py-2 px-2 text-center whitespace-nowrap">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => {
            // Employment bucket drives the row fill. OD overrides regardless
            // of FT/PT. Below/Close performance states still take precedence
            // so coaching flags remain obvious.
            const empKey = p.is_director ? 'OD' : (p.employment_type || 'FT');
            const empFill = EMP_FILL[empKey] || 'transparent';
            const rowBg = p.status === 'Below' ? '#FCF3F3'
                        : p.status === 'Close' ? '#FCF7EA'
                        : empFill;
            const chip = EMP_CHIP[empKey] || EMP_CHIP.FT;
            const isOD = p.is_director;
            const boldCls = isOD ? 'font-bold' : '';
            const isOpen = openRow === p.name;
            return (
              <React.Fragment key={p.name}>
              <tr
                className={`border-t align-middle cursor-pointer transition-colors hover:brightness-[0.97] ${boldCls}`}
                style={{ borderColor: C.border, background: rowBg }}
                data-testid={`mcr-staff-row-${i}`}
                aria-expanded={isOpen}
                onClick={() => setOpenRow(isOpen ? null : p.name)}
              >
                <td className="py-2 px-2 align-middle whitespace-nowrap text-left">
                  <div className="flex items-center gap-1.5">
                    <span className={isOD ? 'font-bold' : 'font-medium'} style={{ color: C.ink }}>{p.name}</span>
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold whitespace-nowrap"
                      style={{ background: chip.bg, color: chip.fg }}
                      data-testid={`mcr-staff-emp-${i}`}
                    >
                      {EMP_LABEL[empKey]}
                    </span>
                  </div>
                  {p.role && <div className="text-[9.5px] leading-tight" style={{ color: C.soft }}>{p.role}</div>}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`}>
                  {p.weekly_target == null ? <span className="italic" style={{ color: C.soft }}>—</span> : INT(p.weekly_target)}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`}>
                  {p.weeks_worked == null
                    ? <span className="italic" style={{ color: C.soft }}>—</span>
                    : <span className={isOD ? '' : 'font-semibold'}>{p.weeks_target_met} of {p.weeks_worked}</span>}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`}>
                  {p.avg_weekly_sessions == null
                    ? <span className="italic" style={{ color: C.soft }}>—</span>
                    : (
                      <>
                        <span className={isOD ? '' : 'font-semibold'} style={{ color: C.ink }}>{p.avg_weekly_sessions}</span>
                        {p.avg_weekly_variance != null && (
                          <span style={{ color: p.avg_weekly_variance >= 0 ? C.teal : C.rose }}>
                            {' '}({p.avg_weekly_variance >= 0 ? '+' : ''}{p.avg_weekly_variance})
                          </span>
                        )}
                      </>
                    )}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`}>
                  {p.monthly_goal == null ? <span className="italic" style={{ color: C.soft }}>—</span> : INT(p.monthly_goal)}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`}>{INT(p.total_sessions)}</td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} ${isOD ? '' : 'font-semibold'} align-middle whitespace-nowrap`}>{INT(p.completed_sessions)}</td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} ${isOD ? '' : 'font-semibold'} align-middle whitespace-nowrap`} style={{ color: p.pct_of_goal == null ? C.soft : (p.pct_of_goal >= 0.90 ? C.teal : C.rose) }}>
                  {p.pct_of_goal == null ? '—' : PCT(p.pct_of_goal)}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`} style={{ color: p.clinician_cancels > 0 ? C.rose : C.soft }}>{INT(p.clinician_cancels)}</td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`} style={{ color: p.notes_flag === 'large_backlog' ? C.amber : C.ink }}>
                  {INT(p.notes_outstanding)}
                </td>
                <td className={`py-2 px-2 text-center ${NUM_CLS} align-middle whitespace-nowrap`} style={{ color: p.notes_overdue > 0 ? C.rose : C.soft }}>
                  {INT(p.notes_overdue)}
                </td>
                <td className="py-2 px-2 text-center align-middle whitespace-nowrap"><StatusPill status={p.status} /></td>
              </tr>
              {isOpen && <WeekBreakdownRow p={p} i={i} weekNotes={weekNotes} onWeekNote={onWeekNote} />}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={12} className="py-6 text-center italic" style={{ color: C.soft }}>No clinicians on staff for this month.</td></tr>
          )}
          <tr className="font-bold" style={{ background: C.paper }}>
            <td colSpan={12} className="p-0"><div className="border-t-2" style={{ borderColor: C.hair }} /></td>
          </tr>
          <tr className="font-bold align-middle" style={{ background: C.paper }}>
            <td className="py-2 px-2 whitespace-nowrap text-left align-middle" style={{ color: C.ink }}>Clinic</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>—</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>
              {staff.clinic_weeks_worked > 0 ? `${staff.clinic_weeks_met} of ${staff.clinic_weeks_worked}` : '—'}
            </td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>
              {staff.clinic_avg_weekly == null
                ? '—'
                : (
                  <>
                    <span>{staff.clinic_avg_weekly}</span>
                    {staff.clinic_avg_weekly_variance != null && (
                      <span style={{ color: staff.clinic_avg_weekly_variance >= 0 ? C.teal : C.rose }}>
                        {' '}({staff.clinic_avg_weekly_variance >= 0 ? '+' : ''}{staff.clinic_avg_weekly_variance})
                      </span>
                    )}
                  </>
                )}
            </td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.monthly_goal)}</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.total_sessions)}</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.completed)}</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>
              {totals.monthly_goal ? PCT(totals.completed_goaled / totals.monthly_goal) : '—'}
            </td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.clinician_cancels)}</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.notes_outstanding)}</td>
            <td className={`py-2 px-2 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: totals.notes_overdue > 0 ? C.rose : C.ink }}>{INT(totals.notes_overdue)}</td>
            <td className="py-2 px-2 text-center whitespace-nowrap align-middle">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── New clients table ───────────────────────────────────────────
function NewClientsTable({ nc, yoyOn }) {
  const rows = [
    { k: 'referrals', label: 'Referrals', unit: 'referral' },
    { k: 'evals_completed', label: 'Evaluations completed', unit: 'evaluation' },
    { k: 'therapy_started', label: 'Therapy starts', unit: 'start' },
  ];
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
      <table className="w-full text-[12px]" data-testid="mcr-new-clients-table">
        <thead>
          <tr className={LABEL} style={{ background: C.paper, color: C.soft }}>
            <th className="py-2 px-3 text-left whitespace-nowrap">Measure</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">This month</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Last month</th>
            {yoyOn && <th className="py-2 px-3 text-center whitespace-nowrap">Last year</th>}
            <th className="py-2 px-3 text-center whitespace-nowrap">vs last month</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const row = nc[r.k];
            return (
              <tr key={r.k} className="border-t align-middle" style={{ borderColor: C.border }}>
                <td className="py-2 px-3 whitespace-nowrap text-left align-middle">{r.label}</td>
                <td className={`py-2 px-3 text-center ${NUM_CLS} font-semibold whitespace-nowrap align-middle`}>{INT(row.current)}</td>
                <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>{INT(row.prior)}</td>
                {yoyOn && (
                  <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>{INT(row.yoy)}</td>
                )}
                <td className="py-2 px-3 text-center whitespace-nowrap align-middle"><CountChangeInline change={row.change} unit={r.unit} /></td>
              </tr>
            );
          })}
          <tr className="border-t align-middle" style={{ borderColor: C.border, background: C.paper }}>
            <td className="py-2 px-3 italic whitespace-nowrap text-left align-middle">Referral → evaluation</td>
            <td className={`py-2 px-3 text-center ${NUM_CLS} font-semibold whitespace-nowrap align-middle`}>{PCT(nc.referral_to_eval.current)}</td>
            <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>{PCT(nc.referral_to_eval.prior)}</td>
            {yoyOn && (
              <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`} style={{ color: C.soft }}>{PCT(nc.referral_to_eval.yoy)}</td>
            )}
            <td className="py-2 px-3 text-center whitespace-nowrap align-middle"><PointsChangeInline change={nc.referral_to_eval.change} /></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Pending Notes table ────────────────────────────────────────
function PendingNotesTable({ pending }) {
  const rows = pending.rows || [];
  const flagLabel = { overdue: 'Overdue', large_backlog: 'Large backlog', pending: 'Pending', clear: 'Clear' };
  const flagColor = { overdue: C.rose, large_backlog: C.amber, pending: C.ink2, clear: C.teal };
  const totals = rows.reduce((a, r) => ({
    outstanding: a.outstanding + (r.outstanding || 0),
    overdue: a.overdue + (r.overdue || 0),
  }), { outstanding: 0, overdue: 0 });
  return (
    <div className="rounded-xl border overflow-x-auto print:overflow-visible" style={{ borderColor: C.border }}>
      <table className="w-full text-[12px]" data-testid="mcr-pending-notes-table">
        <thead>
          <tr className={LABEL} style={{ background: C.paper, color: C.soft }}>
            <th className="py-2 px-3 text-left whitespace-nowrap">Clinician</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Notes waiting</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Overdue</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Oldest (days)</th>
            <th className="py-2 px-3 text-center whitespace-nowrap">Flag</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const rowBg = r.flag === 'overdue' ? '#FCF3F3' : r.flag === 'large_backlog' ? '#FCF7EA' : 'transparent';
            return (
              <tr key={r.clinician} className="border-t align-middle" style={{ borderColor: C.border, background: rowBg }}>
                <td className="py-2 px-3 whitespace-nowrap text-left align-middle" style={{ color: C.ink }}>{r.clinician}</td>
                <td className={`py-2 px-3 text-center ${NUM_CLS} font-semibold align-middle whitespace-nowrap`}
                    style={{ color: r.flag === 'large_backlog' ? C.amber : C.ink }}>
                  {INT(r.outstanding)}
                </td>
                <td className={`py-2 px-3 text-center ${NUM_CLS} font-semibold align-middle whitespace-nowrap`}
                    style={{ color: r.overdue > 0 ? C.rose : C.soft }}>
                  {INT(r.overdue)}
                </td>
                <td className={`py-2 px-3 text-center ${NUM_CLS} align-middle whitespace-nowrap`} style={{ color: C.soft }}>
                  {r.oldest_days == null ? '—' : INT(r.oldest_days)}
                </td>
                <td className="py-2 px-3 text-center align-middle whitespace-nowrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold whitespace-nowrap"
                        style={{ background: `${flagColor[r.flag]}22`, color: flagColor[r.flag] }}>
                    {flagLabel[r.flag]}
                  </span>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center italic" style={{ color: C.soft }}>No clinicians to track.</td></tr>
          )}
          <tr className="font-bold" style={{ background: C.paper }}>
            <td colSpan={5} className="p-0"><div className="border-t-2" style={{ borderColor: C.hair }} /></td>
          </tr>
          <tr className="font-bold align-middle" style={{ background: C.paper }}>
            <td className="py-2 px-3 whitespace-nowrap text-left align-middle" style={{ color: C.ink }}>Clinic total</td>
            <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>{INT(totals.outstanding)}</td>
            <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`}
                style={{ color: totals.overdue > 0 ? C.rose : C.ink }}>{INT(totals.overdue)}</td>
            <td className={`py-2 px-3 text-center ${NUM_CLS} whitespace-nowrap align-middle`}>—</td>
            <td className="py-2 px-3 text-center align-middle whitespace-nowrap">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function defaultMonth() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
